import { AUDIENCE_CODES, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, ChoiceChips, Dialog, DialogContent, Icon, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { useWorkspaceContext } from "../../../session/workspace-context";
import type { EndUserOut } from "../shared/types";
import { useAudiences } from "../shared/use-shared-queries";
import { useUpdateEndUser } from "./use-audiences-data";

const PUBLIC = AUDIENCE_CODES.public;

/** End users always keep the public audience (DEC-024); chips edit the rest. */
export function withPublic(codes: readonly number[]): number[] {
  return [...new Set([PUBLIC, ...codes])].sort((a, b) => a - b);
}

interface Props {
  user: EndUserOut;
  canManage: boolean;
  onClose: () => void;
}

/** End-user actions: view conversations, change audience, block / unblock. */
export function EndUserDialog({ user, canManage, onClose }: Props) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const message = useErrorMessage();
  const audiences = useAudiences();
  const update = useUpdateEndUser();
  const [codes, setCodes] = useState<string[]>(user.audience_codes.filter((c) => c !== PUBLIC).map(String));
  const name = user.display_name ?? t("deployKnowledge.audiences.anonymousUser");
  const blocked = user.status === "blocked";
  const options = (audiences.data ?? []).filter((a) => a.code !== PUBLIC).map((a) => ({ value: String(a.code), label: a.label }));
  const changed = withPublic(codes.map(Number)).join() !== withPublic(user.audience_codes).join();
  const saveAudience = () => {
    update.mutate(
      { id: user.id, body: { audience_codes: withPublic(codes.map(Number)) } },
      {
        onSuccess: () => {
          toast.success(t("deployKnowledge.audiences.audienceChanged"));
          onClose();
        },
        onError: (caught) => toast.error(message(caught)),
      },
    );
  };
  const toggleBlock = () => {
    update.mutate(
      { id: user.id, body: { status: blocked ? "active" : "blocked" } },
      {
        onSuccess: () => {
          toast.success(blocked ? t("deployKnowledge.audiences.unblocked", { name }) : t("deployKnowledge.audiences.blocked", { name }));
          onClose();
        },
        onError: (caught) => toast.error(message(caught)),
      },
    );
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent title={t("deployKnowledge.audiences.userActions")} description={name} closeLabel={t("common.close")}>
        <div className="space-y-4">
          <AppLink
            href={`${consoleRoutes.inbox(summary.slug)}?endUserId=${encodeURIComponent(user.id)}`}
            className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm hover:bg-sunken"
          >
            <Icon name="messages-square" className="size-4 text-fg-muted" />
            {t("deployKnowledge.audiences.viewConversations")}
          </AppLink>
          {canManage ? (
            <>
              <fieldset className="space-y-2 rounded-md border border-line p-3">
                <legend className="px-1 text-sm font-semibold">{t("deployKnowledge.audiences.changeAudience")}</legend>
                <ChoiceChips multiple aria-label={t("deployKnowledge.audiences.changeAudience")} options={options} value={codes} onValueChange={setCodes} />
                <p className="text-xs text-fg-subtle">{t("deployKnowledge.audiences.changeAudienceHint")}</p>
                <Button size="sm" onClick={saveAudience} disabled={!changed} loading={update.isPending}>
                  {t("deployKnowledge.audiences.saveAudience")}
                </Button>
              </fieldset>
              <Button variant={blocked ? "secondary" : "danger"} size="sm" onClick={toggleBlock} disabled={update.isPending}>
                <Icon name={blocked ? "check-circle" : "x-circle"} />
                {blocked ? t("deployKnowledge.audiences.unblock") : t("deployKnowledge.audiences.block")}
              </Button>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
